<template>
  <button @click="show = true" class="btn btn-info btn-sm">
    <i class="fa fa-upload"></i>{{ $t('import') }}
    <b-modal v-model="show" :title="$t('importSitemap')" @close="close" no-close-on-esc no-close-on-backdrop
             no-enforce-focus size="s">
      <div class="form-group">
        <label><b>{{ $t('downloadTemplate') }}</b></label>
        <div>
          <button @click="downloadTemplate" class="btn btn-sm btn-secondary mr-1 mb-2">
            <i class="fa fa-fw fa-lg fa-file-excel-o"></i>sitemap-template.xlsx
          </button>
        </div>
      </div>
      <div class="form-group">
        <label><b>{{ $t('selectFile') }}</b></label>
        <b-form-file class="mb-3"
                     v-model="file"
                     :placeholder="$t('fileInputPlaceholder')"
                     :drop-placeholder="$t('fileInputPlaceholder')"
                     :browse-text="$t('selectFile')"
                     :disabled="reading||totalRows>0"
                     accept="application/x-iwork-keynote-sffnumbers,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ></b-form-file>
      </div>
      <div v-if="reading" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>&nbsp;
        <span>{{ $t('importReadingNotice') }}</span>
      </div>
      <div v-if="importing" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>&nbsp;
        <span>{{ $t('importingNotice') }}</span>
      </div>
      <div class="mb-3" v-if="totalRows">
        <b-progress :max="totalRows">
          <b-progress-bar :value="successCount+errorCount"
                          :label="`${(((successCount + errorCount) / totalRows) * 100).toFixed(0)}%`"></b-progress-bar>
        </b-progress>
        <div class="text-center">{{ successCount + errorCount }}/{{ totalRows }} | {{ $t('error') }}: {{ errorCount }}
          <a @click="downloadErrors" class="btn-link text-primary" href="javascript:">[{{ $t('detail') }}]</a>
        </div>
      </div>
      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="cancel" v-if="!reading&&importing">{{ $t('cancel') }}</button>
        <button class="btn btn-primary" @click="reset" v-if="!reading&&!importing&&totalRows>0">Reset</button>
        <button v-if="totalRows===0" class="btn btn-primary" @click="process" :disabled="reading||importing">
          <b-spinner small v-if="reading||importing"></b-spinner>
          {{ $t('import') }}
        </button>
      </template>
    </b-modal>
  </button>
</template>

<script>
import fileReaderStream from 'filereader-stream';
import {saveAs} from 'file-saver'
import Excel from 'exceljs';

export default {
  data: function() {
    return {
      show: false,
      file: null,
      reading: false,
      importing: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      importErrors: [],
      cancelRequest: false,
    }
  },
  methods: {
    close(evt) {
      if (this.reading || this.importing) {
        evt.preventDefault();
      } else {
        this.reset();
      }
    },
    reset() {
      this.totalRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.importErrors = [];
      this.cancelRequest = false;
      this.reading = false;
      this.importing = false;
      this.file = null;
    },
    cancel() {
      this.cancelRequest = true;
    },
    downloadErrors() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Sitemap errors');
      sheet.columns = [
        {header: this.$t('rowNumber'), key: 'row', width: 15},
        {header: this.$t('errors'), key: 'error', width: 50},
      ];
      for (let err of this.importErrors) {
        let errorMsg = 'Unknown error';
        const error = err.err;
        if (error && error.response) {
          const status = error.response.status;
          if (status === 422 && Array.isArray(error.response.data)) {
            errorMsg = error.response.data
                .map(i => `${i.param}: ${i.msg}`)
                .join(', ');
          } else if (error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          } else {
            errorMsg = `HTTP ${status} Error`;
          }
        } else if (error && error.message) {
          errorMsg = error.message;
        }
        sheet.addRow({
          row: err.row,
          error: errorMsg,
        });
      }
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'sitemap.xlsx'
        )
      });
    },
    getItem(headerRow, row) {
      let item = {active: true};
      for (let j = 1; j <= headerRow.cellCount; j++) {
        let key = headerRow.getCell(j).value;
        let value;
        if (row.getCell(j).isHyperlink) {
          value = row.getCell(j).value.text;
        } else {
          value = row.getCell(j).value;
        }
        if (value) {
          if (typeof value === 'string') {
            value = value.trim();
          }
          item[key] = value;
        }
      }
      return item;
    },
    process(evt) {
      evt.preventDefault();
      if (!this.file) {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('importFileRequired'),
          icon: 'error',
        });
        return;
      }
      if (this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx')) {
        this.reading = true;
        let readStream = fileReaderStream(this.file)
        const workbook = new Excel.Workbook();
        workbook.xlsx.read(readStream).then(async () => {
          if (workbook.worksheets.length === 0) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let sheet = workbook.worksheets[0];
          if (sheet.actualRowCount < 3) {
            throw new Error(this.$t('fileHasNoData'));
          }
          if (!sheet.getRow(1).values.find(v => v && String(v).trim() === 'title')) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let headerRow = sheet.getRow(1);
          let i = 3;
          this.totalRows = sheet.actualRowCount - 2;
          this.reading = false;
          this.importing = true;
          while (i <= sheet.actualRowCount) {
            if (this.cancelRequest) {
              break;
            }
            let item = this.getItem(headerRow, sheet.getRow(i))
            try {
              await this.$bgService.post('/admin-sitemap', item)
              this.successCount ++;
            } catch (err) {
              this.importErrors.push({row: i, err: err});
              this.errorCount ++;
            }
            i++;
          }
        }).catch(err => {
          this.$swal({
            title: this.$t('error'),
            text: err.message,
            icon: 'error',
          })
        }).finally(() => {
          this.reading = false;
          this.importing = false;
          this.cancelRequest = false;
          this.$bus.$emit('reloadSitemap');
        })
      } else {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('invalidFileType'),
          icon: 'error',
        })
      }
    },
    downloadTemplate() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Sitemap');
      this.setupSheet(sheet);
      let row = {
        title: this.$t('title'),
        path: this.$t('path'),
        description: this.$t('description'),
        permission: this.$t('permission'),
        note: this.$t('note'),
      }
      sheet.addRow(row)
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'sitemap-template.xlsx'
        )
      })
    },
    setupSheet(sheet) {
      sheet.columns = [
        {header: 'title', key: 'title', width: 20},
        {header: 'path', key: 'path', width: 50},
        {header: 'description', key: 'description', width: 50},
        {header: 'permission', key: 'permission', width: 50},
        {header: 'note', key: 'note', width: 50},
      ]
    }
  },
  i18n: {
    messages: {
      en: {
        import: 'Import',
        importSitemap: 'Import Sitemap',
        downloadTemplate: 'Download template',
        selectFile: 'Select file',
        fileInputPlaceholder: 'Choose or drag file',
        importReadingNotice: 'Reading data...',
        importingNotice: 'Uploading data...',
        errors: 'Errors',
        detail: 'Detail',
        importFileRequired: 'File required!',
        invalidFileTemplate: 'Invalid file template',
        invalidFileType: 'Invalid file type',
        fileHasNoData: 'File has no data',
        rowNumber: 'Row number',
        title: 'Title',
        path: 'Path',
        description: 'Description',
        permission: 'Permission',
        note: 'Note',
      },
      vi: {
        import: 'Tải lên',
        importSitemap: 'Tải lên danh sách Sitemap',
        downloadTemplate: 'Tải xuống file mẫu',
        selectFile: 'Chọn file',
        fileInputPlaceholder: 'Chọn hoặc kéo thả file',
        importReadingNotice: 'Đang đọc dữ liệu...',
        importingNotice: 'Dữ liệu đang được tải lên...',
        errors: 'Lỗi',
        detail: 'Chi tiết',
        importFileRequired: 'Không được bỏ trống file',
        invalidFileTemplate: 'File sai định dạng',
        invalidFileType: 'Sai loại file',
        fileHasNoData: "File không có dữ liệu. Xin hãy kiểm tra lại!",
        rowNumber: "Số hàng",
        title: 'Tiêu đề',
        path: 'Đường dẫn',
        description: 'Mô tả',
        permission: 'Quyền hạn',
        note: 'Ghi chú',
      }
    }
  }
}
</script>